'use no memo'

import { useLegalEntities, useStoreForm, type StoreFormOutput } from '@gozoki/api'
import {
  COUNTRIES,
  MANUALLY_ASSIGNABLE_STORE_STATUSES,
  STORE_TYPES,
  type Store,
} from '@gozoki/api-types'
import { generateRandomRef, getCountryLabel } from '@gozoki/tools'
import { Button, Checkbox, FormControlLabel, IconButton, Typography } from '@mui/material'
import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import z from 'zod'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import type { PermissionTuple } from '~/utils/ability'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { storeStatusLabel, storeTypeLabel } from '~/utils/types/toFrench'
import { StoreTagsInputRhf } from '../../../components/inputs/StoreTagsInputRhf'
import { useToast } from '../../../components/communication/Toast'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import DatePickerRhf from '../../../components/inputs/DatePickerRhf'
import SelectRhf from '../../../components/inputs/SelectRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

const ADD_STORE: PermissionTuple = ['add', 'stores.store-list.store']
const EDIT_STORE: PermissionTuple = ['edit', 'stores.store-list.store']

export const Route = createFileRoute('/_authenticated/stores/new')({
  // Un seul écran pour la création et l'édition : accessible avec l'un OU l'autre droit.
  staticData: {
    permission: { any: [ADD_STORE, EDIT_STORE] },
  },
  component: AddStorePage,
  validateSearch: z.object({
    storeId: z.number().optional(),
  }),
})
withRoutePermissions(Route)

function AddStorePage() {
  const navigate = Route.useNavigate()
  const { storeId } = Route.useSearch()

  const updateMode = !!storeId

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  // Tuyau infers the raw Lucid model type; the wire payload is the serialized shape (api-types Store).
  const {
    data: rawStore,
    isLoading,
    refetch: refetchStore,
  } = useQuery(api.stores.show.queryOptions(storeId ? { params: { id: storeId } } : skipToken))
  const data = rawStore as Store | undefined

  const { control, trigger, getValues, setValue, register, watch } = useStoreForm()

  const { data: legalEntities = [] } = useLegalEntities()

  const [Toast, showToast] = useToast()

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    // Set all values
    if (data) {
      setValue('checkpointStoreRef', data.checkpointStoreRef)
      setValue('label', data.label)
      setValue('shortLabel', data.shortLabel)
      setValue('address', data.address?.address ?? '')
      setValue('addressAdditionnal', data.address?.addressAdditionnal ?? null)
      setValue('city', data.address?.city ?? '')
      setValue('postalCode', data.address?.postalCode ?? '')
      setValue('country', data.address?.country ?? COUNTRIES[0])
      setValue('storeType', data.storeType)
      setValue('status', data.status)
      setValue('bearcodName', data.bearcodName)
      setValue('legalEntityId', data.legalEntityId)
      setValue('siret', data.siret)
      setValue('analyticCode', data.analyticCode)
      setValue('openingDate', data.openingDate)
      setValue('quantityFactor', data.quantityFactor)
      setValue('growthFactor', data.growthFactor)
      setValue(
        'tagIds',
        (data.tags ?? []).map((tag) => tag.id),
      )
    } else {
      setValue('checkpointStoreRef', generateRandomRef(10))
    }
  }, [setValue, data])

  const navigateBack = useCallback(() => {
    refetchStore()
    navigate({ to: '/stores' })
  }, [navigate, refetchStore])

  // ******************************************************************** //
  //                              MUTATIONS                               //
  // ******************************************************************** //

  const { mutate: upload, isPending: isUploading } = useMutation(
    api.stores.store.mutationOptions({
      onError: (error) => {
        showToast({
          severity: 'error',
          message: getErrorMessageWithSentry(error),
        })
      },
      onSuccess: navigateBack,
    }),
  )

  const { mutate: update, isPending: isUpdating } = useMutation(
    api.stores.update.mutationOptions({
      onError: (error) => {
        showToast({
          severity: 'error',
          message: getErrorMessageWithSentry(error),
        })
      },
      onSuccess: navigateBack,
    }),
  )

  // ******************************************************************** //
  //                               HANDLERS                               //
  // ******************************************************************** //

  const uploadStore = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid) {
      upload({ body: getValues() as StoreFormOutput })
    }
  }

  const handleUpdateStore = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid && storeId) {
      update({
        params: { id: storeId },
        body: getValues() as StoreFormOutput,
      })
    }
  }

  const createShippingValue = watch('createShipping')
  const storeTypeValue = watch('storeType')

  return (
    <Page sx={{ margin: 'auto', maxWidth: '50vw' }}>
      <Toast />
      <div style={{ alignItems: 'center', display: 'flex' }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
          {updateMode ? 'Modifier une boutique' : 'Ajouter une boutique'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={navigateBack}
        >
          Annuler
        </Button>
        <AuthorizedButton
          permission={updateMode ? EDIT_STORE : ADD_STORE}
          variant="contained"
          disabled={isUpdating || isUploading}
          onClick={updateMode ? handleUpdateStore : uploadStore}
        >
          {updateMode ? 'Modifier' : 'Ajouter'}
        </AuthorizedButton>
      </div>
      <LoadingPaper
        sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
        loading={isLoading && updateMode}
      >
        <Typography mb="8px" style={fonts.inputHeader}>
          Référence
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Ref"
          color="info"
          control={control}
          trigger={trigger}
          name="checkpointStoreRef"
          fullWidth
          disabled
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Nom
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Nom de la boutique"
          color="info"
          control={control}
          trigger={trigger}
          name="label"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Nom court
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Nom court de la boutique"
          color="info"
          control={control}
          trigger={trigger}
          name="shortLabel"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Adresse
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Adresse"
          color="info"
          control={control}
          trigger={trigger}
          name="address"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Adresse additionnelle
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Adresse"
          color="info"
          control={control}
          trigger={trigger}
          name="addressAdditionnal"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Ville
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Adresse"
          color="info"
          control={control}
          trigger={trigger}
          name="city"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Code postal
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Adresse"
          color="info"
          control={control}
          trigger={trigger}
          name="postalCode"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Pays
        </Typography>
        <SelectRhf
          control={control}
          name="country"
          options={COUNTRIES.map((country) => ({
            label: getCountryLabel(country),
            value: country,
          }))}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Référence Bearcod
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Référence Bearcod"
          color="info"
          control={control}
          trigger={trigger}
          name="bearcodName"
          fullWidth
        />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Type de boutique
        </Typography>
        <SelectRhf
          control={control}
          name="storeType"
          options={STORE_TYPES.map((type) => ({
            label: storeTypeLabel[type],
            value: type,
          }))}
        />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Entreprise Légal
        </Typography>

        <SelectRhf
          control={control}
          name="legalEntityId"
          options={legalEntities.map((entity) => ({
            label: entity.name,
            value: entity.id,
          }))}
        />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          SIRET
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="SIRET (14 chiffres)"
          color="info"
          control={control}
          trigger={trigger}
          name="siret"
          fullWidth
        />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Code analytique
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Code de section analytique (exports comptables)"
          color="info"
          control={control}
          trigger={trigger}
          name="analyticCode"
          fullWidth
        />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Statut
        </Typography>
        {updateMode && data?.status === 'PERMANENTLY_CLOSED' && (
          <Typography color="text.secondary">
            {storeStatusLabel.PERMANENTLY_CLOSED} (statut historique conservé)
          </Typography>
        )}
        {updateMode && data?.status !== 'PERMANENTLY_CLOSED' && (
          <SelectRhf
            control={control}
            name="status"
            disableClearable
            options={MANUALLY_ASSIGNABLE_STORE_STATUSES.map((status) => ({
              label: storeStatusLabel[status],
              value: status,
            }))}
          />
        )}
        {!updateMode && (
          <Typography color="text.secondary">
            Le statut sera déterminé automatiquement à partir de la date d'ouverture.
          </Typography>
        )}

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Date d'ouverture
        </Typography>
        <DatePickerRhf control={control} name="openingDate" trigger={trigger} />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Facteur de quantité pour l'ouverture (0.8 si c'est un petit magasin, 1.2 si c'est un grand
          magasin)
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Facteur de quantité"
          color="info"
          control={control}
          trigger={trigger}
          name="quantityFactor"
          type="number"
          inputProps={{
            max: 2,
            min: 0,
            step: 0.1,
          }}
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Taux de croissance
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Taux de croissance"
          color="info"
          control={control}
          trigger={trigger}
          name="growthFactor"
          type="number"
          inputProps={{
            max: 2,
            min: 0,
            step: 0.1,
          }}
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Tags
        </Typography>
        <StoreTagsInputRhf control={control} name="tagIds" />
        {!updateMode && (
          <>
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Action supplémentaire
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  {...register('createShipping')}
                  checked={createShippingValue}
                  disabled={storeTypeValue !== 'BOUTIQUE'}
                />
              }
              label="Créer automatiquement un stock transport associé"
            />
          </>
        )}
      </LoadingPaper>
    </Page>
  )
}
